import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Building2, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import { PortalPage, PortalHero, InlineError, GoldButton } from "./portal/shared";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  usePageTitle("Forgot Password");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const context = new URLSearchParams(window.location.search).get("context");
  const isPortal = context === "portal";
  const loginHref = isPortal ? "/portal/login" : "/login";

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordForm) => {
      const response = await apiRequest("POST", "/api/auth/forgot-password", { ...data, ...(context ? { context } : {}) });
      return response;
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to process request. Please try again.");
    },
  });

  const handleSubmit = (data: ForgotPasswordForm) => {
    setError(null);
    forgotPasswordMutation.mutate(data);
  };

  if (isPortal) {
    if (success) {
      return (
        <PortalPage>
          <PortalHero
            eyebrow="Don Norman Associates"
            title={<>Service Bureau<br />Portal</>}
            subtitle="Secure access for authorized Service Bureau clients."
          />
          <div style={{ padding: "0 24px 64px", maxWidth: 440, margin: "-48px auto 0", position: "relative" }}>
            <div style={{ background: "white", borderRadius: 16, boxShadow: "0 20px 60px rgba(27,43,75,0.18)", padding: "40px 48px" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 56, height: 56, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <CheckCircle size={28} color="#16a34a" />
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 8 }}>
                  Check Your Email
                </h2>
                <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
                  If an account exists with that email address, we've sent instructions to reset your password.
                </p>
                <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <Mail size={14} />
                  Check your inbox and spam folder
                </p>
                <a
                  href={loginHref}
                  data-testid="link-back-to-login"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: "#C9962A", textDecoration: "none", fontWeight: 600 }}
                >
                  <ArrowLeft size={14} />
                  Back to Login
                </a>
              </div>
            </div>
          </div>
        </PortalPage>
      );
    }

    return (
      <PortalPage>
        <PortalHero
          eyebrow="Don Norman Associates"
          title={<>Service Bureau<br />Portal</>}
          subtitle="Secure access for authorized Service Bureau clients."
        />
        <div style={{ padding: "0 24px 64px", maxWidth: 440, margin: "-48px auto 0", position: "relative" }}>
          <div style={{ background: "white", borderRadius: 16, boxShadow: "0 20px 60px rgba(27,43,75,0.18)", padding: "40px 48px" }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ width: 48, height: 48, background: "#C9962A", borderRadius: 10, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 900, fontSize: 18, color: "white" }}>
                DNA
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 4 }}>
                Forgot Password
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280" }}>
                Enter your email and we'll send a reset link
              </p>
            </div>

            {error && <InlineError message={error} />}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#1B2B4B", display: "block", marginBottom: 6 }}>
                        Email Address <span style={{ color: "#C9962A" }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <input
                          type="email"
                          placeholder="you@company.com"
                          data-testid="input-email"
                          {...field}
                          style={{ width: "100%", padding: "13px 16px", fontSize: 15, color: "#1a1a1a", background: "white", border: "1.5px solid #e2ddd6", borderRadius: 10, outline: "none", boxSizing: "border-box" }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <GoldButton
                  type="submit"
                  disabled={forgotPasswordMutation.isPending}
                  data-testid="button-send-reset-link"
                >
                  {forgotPasswordMutation.isPending ? (
                    <>
                      <Loader2 size={18} style={{ animation: "spin 0.8s linear infinite" }} />
                      Sending…
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </GoldButton>
              </form>
            </Form>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              <a
                href={loginHref}
                data-testid="link-back-to-login"
                style={{ fontSize: 13, color: "#C9962A", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                <ArrowLeft size={13} />
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </PortalPage>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Check Your Email</CardTitle>
            <CardDescription className="text-center">
              If an account exists with that email address, we've sent instructions to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              <Mail className="h-5 w-5 inline-block mr-2" />
              Check your inbox and spam folder
            </div>
            <Link href={loginHref}>
              <Button variant="outline" className="w-full" data-testid="link-back-to-login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Forgot Password</CardTitle>
          <CardDescription className="text-center">
            Enter your email address and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        data-testid="input-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button
                type="submit"
                className="w-full"
                disabled={forgotPasswordMutation.isPending}
                data-testid="button-send-reset-link"
              >
                {forgotPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>
          </Form>
          
          <div className="text-center">
            <Link href={loginHref} className="text-sm text-primary hover:underline" data-testid="link-back-to-login">
              <ArrowLeft className="h-4 w-4 inline-block mr-1" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
